import React from 'react';
import { Loader2 } from '@/components/icons';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'warning' | 'info';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs',
  secondary:
    'bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border shadow-xs',
  outline:
    'border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-card dark:border-border dark:hover:bg-secondary',
  ghost:
    'hover:bg-accent hover:text-accent-foreground dark:hover:bg-secondary',
  danger:
    'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-xs',
  warning:
    'bg-warning-strong text-white shadow-xs hover:bg-warning-strong/90',
  info:
    'bg-info-strong text-white shadow-xs hover:bg-info-strong/90',
};

const sizeStyles: Record<ButtonSize, string> = {
  xs: 'h-7 text-xs px-2.5 rounded-md gap-1.5 font-medium',
  sm: 'h-8 text-xs px-3 rounded-lg gap-1.5',
  md: 'h-9 text-sm px-4 rounded-lg gap-2',
  lg: 'h-11 text-base px-6 rounded-lg gap-2.5',
  icon: 'size-9 rounded-lg',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-all duration-150 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring select-none active:scale-[0.98]',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        ) : (
          leftIcon && (
            <span className="shrink-0">
              {React.isValidElement(leftIcon)
                ? leftIcon
                : typeof leftIcon === 'function' ||
                  (typeof leftIcon === 'object' && leftIcon !== null && '$$typeof' in leftIcon)
                ? React.createElement(leftIcon as unknown as React.ElementType, { className: 'h-4 w-4 shrink-0' })
                : leftIcon}
            </span>
          )
        )}
        {children}
        {!isLoading && rightIcon && (
          <span className="shrink-0">
            {React.isValidElement(rightIcon)
              ? rightIcon
              : typeof rightIcon === 'function' ||
                (typeof rightIcon === 'object' && rightIcon !== null && '$$typeof' in rightIcon)
              ? React.createElement(rightIcon as unknown as React.ElementType, { className: 'h-4 w-4 shrink-0' })
              : rightIcon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
